/**
 * Utility functions for image handling and conversion
 */

/**
 * Convert a data URL (base64) to a File object
 * @param dataUrl - The data URL string (e.g., "data:image/png;base64,...")
 * @param filename - The desired filename for the File object
 * @returns File object created from the data URL
 */
export const dataURLtoFile = (dataUrl: string, filename: string): File => {
  // Check if dataUrl is valid
  if (!dataUrl || !dataUrl.includes(",")) {
    throw new Error("Invalid data URL");
  }

  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

/**
 * Convert multiple base64 images to File objects
 * @param images - Object containing base64 image strings
 * @param baseFilename - Base filename to use (will append image type)
 * @returns Object with same keys but File objects as values
 */
export const convertImagesToFiles = (
  images: Record<string, string>,
  baseFilename: string = "document",
): Record<string, File> => {
  const files: Record<string, File> = {};

  for (const [key, dataUrl] of Object.entries(images)) {
    if (dataUrl && dataUrl.startsWith("data:")) {
      try {
        // Detect extension from mime type
        const mimeMatch = dataUrl.match(/data:image\/(.*?);/);
        const extension = mimeMatch ? mimeMatch[1] : "png";
        const filename = `${baseFilename}_${key}.${extension}`;

        files[key] = dataURLtoFile(dataUrl, filename);
      } catch (error) {
        console.error(`Failed to convert image ${key}:`, error);
      }
    }
  }

  return files;
};

/**
 * Get the best quality document image from available images
 * Priority: White light > IR > UV
 * @param images - Object containing various image types
 * @returns The best quality image data URL or null
 */
export const getBestDocumentImage = (images: {
  whiteImage?: string;
  irImage?: string;
  uvImage?: string;
  fullDocumentImage?: string;
}): string | null => {
  return (
    images.fullDocumentImage ||
    images.whiteImage ||
    images.irImage ||
    images.uvImage ||
    null
  );
};

/**
 * Get the best quality head photo from available photos
 * Priority: Chip > OCR > SID
 * @param images - Object containing various photo types
 * @returns The best quality photo data URL or null
 */
export const getBestHeadPhoto = (images: {
  chipHeadImage?: string;
  ocrHeadImage?: string;
  sidHeadImage?: string;
  photo?: string;
}): string | null => {
  return (
    images.chipHeadImage ||
    images.ocrHeadImage ||
    images.sidHeadImage ||
    images.photo ||
    null
  );
};

/**
 * Check if a string is a valid data URL
 * @param str - String to check
 * @returns true if valid data URL, false otherwise
 */
export const isValidDataURL = (str: string): boolean => {
  if (!str || typeof str !== "string") return false;
  return str.startsWith("data:") && str.includes(",");
};

/**
 * Get file extension from data URL
 * @param dataUrl - Data URL string
 * @returns File extension (e.g., "png", "jpg") or "png" as default
 */
export const getExtensionFromDataURL = (dataUrl: string): string => {
  const mimeMatch = dataUrl.match(/data:image\/(.*?);/);
  if (mimeMatch) {
    const mime = mimeMatch[1];
    // Handle common variations
    if (mime === "jpeg") return "jpg";
    return mime;
  }
  return "png";
};

/**
 * Estimate file size from data URL (in bytes)
 * @param dataUrl - Data URL string
 * @returns Estimated file size in bytes
 */
export const estimateDataURLSize = (dataUrl: string): number => {
  if (!isValidDataURL(dataUrl)) return 0;

  // Base64 encoding increases size by ~33%
  const base64 = dataUrl.split(",")[1];
  const padding = (base64.match(/=/g) || []).length;
  return Math.floor((base64.length * 3) / 4 - padding);
};

/**
 * Format file size to human-readable string
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};
